import { Link, Outlet, useLocation, useRouteLoaderData } from '@remix-run/react';
import { useState } from 'react';
import { Alert } from '#app/components/Alert.tsx';
import { Can } from '#app/components/permission/can.tsx';
import { PermissionsProvider } from '#app/components/permission/permissionContext.tsx';
import UserSidebar from '#app/layout/user/UserSidebar';
import { type loader as rootLoader } from '#app/root.tsx';
import { useUser } from '#app/utils/user.ts';

export default function UserLayout() {
  const [open, setOpen] = useState(false);
  const rootData = useRouteLoaderData<typeof rootLoader>('root');
  const user = useUser();
  const allowedPermissions = user?.roles.flatMap((role) =>
    role.permissions.map((permission) =>
      [permission.action, permission.entity, permission.access].filter(Boolean).join(':')
    )
  );

  const metadataCapabilities = rootData?.metadata?.capabilities;

  // Note: The UserSidebar is using `fixed` if the screensize is `sm` or larger so the body needs to have a `ml` class to push the content to the right

  return (
    <PermissionsProvider
      allowedPermissions={[
        ...(metadataCapabilities?.length ? metadataCapabilities : []),
        ...(allowedPermissions || []),
      ]}
    >
      <div className="sm:flex-start h-dvh sm:flex sm:flex-1 sm:flex-row">
        <UserSidebar open={open} setOpen={setOpen} />
        <div className={`w-full sm:ml-64 print:ml-0 ${open ? 'hidden' : ''}`}>
          <div className="container mb-32 mt-8 sm:my-8">
            <Can I="confirm" a="email" scope="own">
              <EmailConfirmationAlert />
            </Can>
            <Outlet />
          </div>
        </div>
      </div>
    </PermissionsProvider>
  );
}

function EmailConfirmationAlert() {
  const location = useLocation();
  if (location.pathname.includes('/member/settings/email-confirmation') || location.pathname.includes('/verify')) {
    return null;
  }

  return (
    <Alert variant="warn" className="mb-4">
      <span className="font-bold">Confirm your email address</span> through the link in the email we sent you. Click{' '}
      <Link to="/member/settings/email-confirmation" className="underline hover:text-primary">
        here
      </Link>{' '}
      to resend the email.
    </Alert>
  );
}
